import React, { useEffect, useState } from 'react';
import classes from '../AllSites/allSitesSearchBar.module.css';
import filters from '../../assets/Portal Iconnography SVGs/PortalAppRealted _Icons/misc/filters2.png';
import { Dropdown } from 'primereact/dropdown';

// ── helpers ────────────────────────────────────────────────────

const toggle = (arr, val) =>
  arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

const Section = ({ label, badge, open, onToggle, children }) => (
  <div className={classes.section}>
    <div className={classes.sectionHeader} onClick={onToggle}>
      <div className={classes.sectionHeaderLeft}>
        <span className={classes.sectionLabel}>{label}</span>
        {badge > 0 && <span className={classes.badge}>{badge}</span>}
      </div>
      <span className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`}>▼</span>
    </div>
    {open && <div className={classes.sectionBody}>{children}</div>}
  </div>
);

const Pills = ({ options, selected, onToggle }) => (
  <div className={classes.pills}>
    {options.map(opt => (
      <button
        key={opt}
        type="button"
        className={`${classes.pill} ${selected.includes(opt) ? classes.pillActive : ''}`}
        onClick={() => onToggle(opt)}
      >
        {opt}
      </button>
    ))}
  </div>
);

const CheckRow = ({ label, checked, onChange }) => (
  <label className={classes.checkRow}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    {label}
  </label>
);

const INTEGRATION_TYPES = [
  { label: 'AJAX', value: 'AJAX' },
  { label: 'Dahua', value: 'DAHUA' },
  { label: 'Hikvision', value: 'HIKVISION' },
  { label: 'AIBox', value: 'AIBOX' },
  { label: 'Camect', value: 'CAMECT' },
];

// ── Component ──────────────────────────────────────────────────

const CCTVIntegrationsSearchBar = (props) => {
  const {
    placeholder,
    onChange,
    value,
    handleFocus,
    handleBlur,
    showFilterButton,
    groups,
    online, setOnline,
    offline, setOffline,
    deviceTag, setDeviceTag,
    integrationType, setIntegrationType,
    connectionIssues, setConnectionIssues,
    armedState, setArmedState,
    outputStatus, setOutputStatus,
    groupName, setGroupName,
    submit, setSubmit
  } = props;

  const [showFilters, setShowFilters] = useState(false);
  const [openSections, setOpenSections] = useState({});

  // ── local filter state ──────────────────────────────────────
  const [localDeviceTag, setLocalDeviceTag] = useState(deviceTag);
  const [localGroupName, setLocalGroupName] = useState(groupName);
  const [localIntegrationType, setLocalIntegrationType] = useState(integrationType);
  const [localConnectionIssues, setLocalConnectionIssues] = useState(connectionIssues);
  const [localArmedState, setLocalArmedState] = useState(armedState ?? null);
  const [localOutputStatus, setLocalOutputStatus] = useState(outputStatus ?? null);

  // Multi-select pill array
  const [siteStatusFilters, setSiteStatusFilters] = useState([]);

  // sync from props
  useEffect(() => {
    setLocalDeviceTag(deviceTag);
    setLocalGroupName(groupName);
    setLocalIntegrationType(integrationType);
    setLocalConnectionIssues(connectionIssues);
    setLocalArmedState(armedState ?? null);
    setLocalOutputStatus(outputStatus ?? null);
    setSiteStatusFilters([...(online ? ['Online'] : []), ...(offline ? ['Offline'] : [])]);
  }, [online, offline, deviceTag, groupName, integrationType, connectionIssues, armedState, outputStatus]);

  const filterDefaults = {
    online: 0, offline: 0, deviceTag: null, groupName: null,
    integrationType: null, connectionIssues: 0, armedState: null, outputStatus: null
  };

  const setters = {
    online: setOnline, offline: setOffline, deviceTag: setDeviceTag,
    groupName: setGroupName, integrationType: setIntegrationType,
    connectionIssues: setConnectionIssues, armedState: setArmedState,
    outputStatus: setOutputStatus
  };

  const toggleSection = (key) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleClear = () => onChange({ target: { value: '' } });

  const handleShowFilters = () => setShowFilters(p => !p);

  const HandleResetFilters = () => {
    setLocalDeviceTag(null);
    setLocalGroupName(null);
    setLocalIntegrationType(null);
    setLocalConnectionIssues(0);
    setLocalArmedState(null);
    setLocalOutputStatus(null);
    setSiteStatusFilters([]);

    Object.entries(setters).forEach(([key, setter]) => setter(filterDefaults[key]));

    setSubmit(submit + 1);
    setShowFilters(false);
  };

  const HandleApplyFilters = () => {
    const payload = {
      online: siteStatusFilters.includes('Online') ? 1 : 0,
      offline: siteStatusFilters.includes('Offline') ? 1 : 0,
      deviceTag: localDeviceTag,
      groupName: localGroupName,
      integrationType: localIntegrationType,
      connectionIssues: localConnectionIssues,
      armedState: localArmedState,
      outputStatus: localOutputStatus
    };
    Object.entries(payload).forEach(([key, val]) => setters[key]?.(val));

    setShowFilters(false);
    setSubmit(submit + 1);
  };

  // ── badge counts ────────────────────────────────────────────
  const statusBadge = siteStatusFilters.length;
  const integrationBadge = (localIntegrationType ? 1 : 0) + (localConnectionIssues ? 1 : 0);
  const armedBadge = localArmedState !== null ? 1 : 0;
  const outputStatusBadge = localOutputStatus !== null ? 1 : 0;
  const groupBadge = localGroupName ? 1 : 0;
  const detailsBadge = localDeviceTag ? 1 : 0;

  const clearFilter = (key) => {
    const setter = setters[key];
    if (setter !== undefined) setter(filterDefaults[key]);
    setSubmit(submit + 1);
  };

  const activeChips = [
    online && { key: 'online', label: 'Online' },
    offline && { key: 'offline', label: 'Offline' },
    integrationType && { key: 'integrationType', label: INTEGRATION_TYPES.find(t => t.value === integrationType)?.label ?? integrationType },
    connectionIssues && { key: 'connectionIssues', label: 'CCTV connection issues' },
    armedState === 1 && { key: 'armedState', label: 'Disarmed' },
    armedState === 0 && { key: 'armedState', label: 'Armed' },
    outputStatus === 1 && { key: 'outputStatus', label: 'Output visible' },
    outputStatus === 0 && { key: 'outputStatus', label: 'Output not visible' },
    groupName && { key: 'groupName', label: `Group: ${groupName}` },
    deviceTag && { key: 'deviceTag', label: `Portal name: "${deviceTag}"` },
  ].filter(Boolean);

  const handleKeyPress = (event) => { if (event.key === 'Enter') event.preventDefault(); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <div className={classes.searchContainer}>
        <input
          className={classes.searchBar}
          placeholder={placeholder}
          value={value}
          onKeyPress={handleKeyPress}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          spellCheck={false}
        />

        <div className={classes.buttonGroup}>
          {value && (
            <button className={classes.clearButton} onClick={handleClear} type="button">×</button>
          )}
          {showFilterButton !== false && (
            <button className={classes.filterButton} onClick={handleShowFilters} type="button">
              <img style={{ height: 30, width: 30 }} src={filters} />
            </button>
          )}
        </div>

        {showFilters && (
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <span className={classes.panelTitle}>Filters</span>
              <div className={classes.panelActions}>
                <button className={classes.resetBtn} type="button" onClick={HandleResetFilters}>Reset</button>
                <button className={classes.applyBtn} type="button" onClick={HandleApplyFilters}>Apply</button>
              </div>
            </div>

            <div className={classes.panelBody}>

              {/* Status */}
              <Section label="Status" badge={statusBadge} open={openSections.status} onToggle={() => toggleSection('status')}>
                <Pills
                  options={['Online', 'Offline']}
                  selected={siteStatusFilters}
                  onToggle={v => setSiteStatusFilters(toggle(siteStatusFilters, v))}
                />
              </Section>

              {/* Integration type */}
              <Section label="Integration type" badge={integrationBadge} open={openSections.integration} onToggle={() => toggleSection('integration')}>
                <Pills
                  options={INTEGRATION_TYPES.map(t => t.label)}
                  selected={INTEGRATION_TYPES.filter(t => t.value === localIntegrationType).map(t => t.label)}
                  onToggle={label => {
                    const type = INTEGRATION_TYPES.find(t => t.label === label);
                    setLocalIntegrationType(localIntegrationType === type.value ? null : type.value);
                  }}
                />
                <CheckRow
                  label="Only show sites with CCTV connection issues"
                  checked={localConnectionIssues === 1}
                  onChange={() => setLocalConnectionIssues(localConnectionIssues === 1 ? 0 : 1)}
                />
              </Section>

              {/* Armed / Disarmed */}
              <Section label="Armed / Disarmed" badge={armedBadge} open={openSections.armed} onToggle={() => toggleSection('armed')}>
                <Pills
                  options={['Armed', 'Disarmed']}
                  selected={localArmedState === 0 ? ['Armed'] : localArmedState === 1 ? ['Disarmed'] : []}
                  onToggle={v => {
                    if (v === 'Armed') setLocalArmedState(localArmedState === 0 ? null : 0);
                    else setLocalArmedState(localArmedState === 1 ? null : 1);
                  }}
                />
              </Section>

              {/* Output status */}
              <Section label="Output status" badge={outputStatusBadge} open={openSections.outputStatus} onToggle={() => toggleSection('outputStatus')}>
                <Pills
                  options={['Visible', 'Not visible']}
                  selected={localOutputStatus === 1 ? ['Visible'] : localOutputStatus === 0 ? ['Not visible'] : []}
                  onToggle={v => {
                    if (v === 'Visible') setLocalOutputStatus(localOutputStatus === 1 ? null : 1);
                    else setLocalOutputStatus(localOutputStatus === 0 ? null : 0);
                  }}
                />
              </Section>

              {/* Group */}
              <Section label="Group" badge={groupBadge} open={openSections.group} onToggle={() => toggleSection('group')}>
                <div className={classes.fieldRow}>
                  <Dropdown
                    value={localGroupName}
                    className="w-full md:w-14rem"
                    style={{ width: '100%' }}
                    onChange={(e) => setLocalGroupName(e.value)}
                    options={groups.map(g => g.name).sort((a, b) => a.localeCompare(b))}
                    placeholder="Choose group"
                    filter
                    showClear
                  />
                </div>
              </Section>

              {/* Site details */}
              <Section label="Site details" badge={detailsBadge} open={openSections.details} onToggle={() => toggleSection('details')}>
                <div className={classes.fieldRow}>
                  <span className={classes.fieldLabel}>Portal name contains</span>
                  <input
                    className={classes.textInput}
                    placeholder="Enter keyword..."
                    value={localDeviceTag || ''}
                    onChange={e => setLocalDeviceTag(e.target.value)}
                  />
                </div>
              </Section>

            </div>
          </div>
        )}
      </div>

      {activeChips.length > 0 && (
        <div className={classes.activeFilters}>
          {activeChips.map(({ key, label }) => (
            <span key={key} className={classes.filterChip}>
              {label}
              <button className={classes.filterChipClear} onClick={() => clearFilter(key)} type="button">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default CCTVIntegrationsSearchBar;
